import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../components/Admin/AdminLayout';
import projectService from '../../services/projectService';
import API_CONFIG from '../../config/api';
import ImageUpload from '../../components/Common/ImageUpload';

export default function CaseStudyForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = !!id;

    const [projectId, setProjectId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEditMode);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        image: '',
        overview: '',
        description: '',
        manager: { name: '', role: '', image: '' },
        images: [],
        requirements: {
            title: '',
            challenge: { title: 'The Challenge', description: '' },
            solution: { title: 'Our Solution', description: '' },
            impact: { title: 'Impact', description: '' }
        },
        results: {
            title: '',
            description: '',
            images: [], // Changed from single image to array
            items: ['', '', '', ''] // Default 4 items
        },
        related_projects: [],
        faqs: [] // Array of { question: '', answer: '' }
    });

    useEffect(() => {
        if (isEditMode) {
            const fetchProject = async () => {
                try {
                    // Fetch by slug (which is passed as 'id' param from URL)
                    const response = await projectService.getProjectBySlug(id);
                    const found = response.data || response;

                    if (found) {
                        setProjectId(found.id); // Store the numeric ID for updates
                        setFormData(prev => ({
                            ...prev,
                            ...found,
                            manager: { ...prev.manager, ...(found.manager || {}) },
                            // Ensure images is an array (handle legacy video object data)
                            images: Array.isArray(found.images) ? found.images : [],
                            requirements: { ...prev.requirements, ...(found.requirements || {}) },
                            results: {
                                ...prev.results,
                                ...(found.results || {}),
                                // Ensure images array exists, migrate legacy single image if needed
                                images: Array.isArray(found.results?.images)
                                    ? found.results.images
                                    : (found.results?.image ? [found.results.image] : [])
                            },
                            faqs: Array.isArray(found.faqs) ? found.faqs : []
                        }));
                    } else {
                        setError('Project not found');
                    }
                } catch (err) {
                    console.error(err);
                    setError('Failed to fetch project');
                } finally {
                    setFetching(false);
                }
            };
            fetchProject();
        }
    }, [id, isEditMode]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleNestedChange = (parent, field, value) => {
        setFormData(prev => ({
            ...prev,
            [parent]: { ...prev[parent], [field]: value }
        }));
    };

    const handleDeepNestedChange = (parent, sub, field, value) => {
        setFormData(prev => ({
            ...prev,
            [parent]: {
                ...prev[parent],
                [sub]: { ...prev[parent][sub], [field]: value }
            }
        }));
    };

    const handleResultItemChange = (index, value) => {
        const newItems = [...formData.results.items];
        newItems[index] = value;
        handleNestedChange('results', 'items', newItems);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (isEditMode) {
                // Use the stored projectId for the update call
                await projectService.updateProject(projectId, formData);
            } else {
                await projectService.createProject(formData);
            }
            navigate('/admin/case-studies');
        } catch (err) {
            console.error(err);
            setError(err.message || 'Failed to save project');
        } finally {
            setLoading(false);
        }
    };

    // Helper for image upload (using basic text for URL now, or existing upload endpoint if available)
    // Assuming backend has /upload/image. I'll use a simple text input for URL for now to keep it simple as asked in plan
    // unless I see BlogForm has a reuseable uploader.

    if (fetching) return <AdminLayout>Loading...</AdminLayout>;

    return (
        <AdminLayout>
            <div className="max-w-4xl mx-auto pb-10">
                    <h1 className="text-2xl font-bold text-white mb-6">
                        {isEditMode ? 'Edit Case Study' : 'New Case Study'}
                    </h1>

                    {error && (
                        <div className="bg-red-500/10 border border-red-500/50 p-4 rounded-lg mb-6">
                            <p className="text-red-400">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Basic Info */}
                        <div className="bg-[#1a1025] p-6 rounded-xl border border-white/5 space-y-4">
                            <h2 className="text-lg font-semibold text-white mb-4">Basic Information</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-1">Title</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-1">Slug</label>
                                    <input
                                        type="text"
                                        name="slug"
                                        value={formData.slug}
                                        onChange={handleChange}
                                        className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white"
                                        required
                                    />
                                </div>

                                <div>
                                    <ImageUpload
                                        label="Main Image"
                                        value={formData.image}
                                        onChange={(url) => handleChange({ target: { name: 'image', value: url } })}
                                    />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-400 mb-1">Overview</label>
                                    <textarea
                                        name="overview"
                                        value={formData.overview}
                                        onChange={handleChange}
                                        rows={3}
                                        className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-400 mb-1">Full Description</label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        rows={5}
                                        className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white"
                                    />
                                </div>
                            </div>
                        </div>



                        {/* Requirements */}
                        <div className="bg-[#1a1025] p-6 rounded-xl border border-white/5 space-y-4">
                            <h2 className="text-lg font-semibold text-white">Requirements & Impact</h2>
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Section Title</label>
                                <input
                                    type="text"
                                    value={formData.requirements.title}
                                    onChange={(e) => handleNestedChange('requirements', 'title', e.target.value)}
                                    className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Challenge */}
                                <div className="space-y-2">
                                    <h3 className="text-white font-medium">Challenge</h3>
                                    <input
                                        type="text"
                                        placeholder="Title"
                                        value={formData.requirements.challenge.title}
                                        onChange={(e) => handleDeepNestedChange('requirements', 'challenge', 'title', e.target.value)}
                                        className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white text-sm"
                                    />
                                    <textarea
                                        placeholder="Description"
                                        value={formData.requirements.challenge.description}
                                        onChange={(e) => handleDeepNestedChange('requirements', 'challenge', 'description', e.target.value)}
                                        rows={4}
                                        className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white text-sm"
                                    />
                                </div>
                                {/* Solution */}
                                <div className="space-y-2">
                                    <h3 className="text-white font-medium">Solution</h3>
                                    <input
                                        type="text"
                                        placeholder="Title"
                                        value={formData.requirements.solution.title}
                                        onChange={(e) => handleDeepNestedChange('requirements', 'solution', 'title', e.target.value)}
                                        className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white text-sm"
                                    />
                                    <textarea
                                        placeholder="Description"
                                        value={formData.requirements.solution.description}
                                        onChange={(e) => handleDeepNestedChange('requirements', 'solution', 'description', e.target.value)}
                                        rows={4}
                                        className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white text-sm"
                                    />
                                </div>
                                {/* Impact */}
                                <div className="space-y-2">
                                    <h3 className="text-white font-medium">Impact</h3>
                                    <input
                                        type="text"
                                        placeholder="Title"
                                        value={formData.requirements.impact.title}
                                        onChange={(e) => handleDeepNestedChange('requirements', 'impact', 'title', e.target.value)}
                                        className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white text-sm"
                                    />
                                    <textarea
                                        placeholder="Description"
                                        value={formData.requirements.impact.description}
                                        onChange={(e) => handleDeepNestedChange('requirements', 'impact', 'description', e.target.value)}
                                        rows={4}
                                        className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white text-sm"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Results */}
                        <div className="bg-[#1a1025] p-6 rounded-xl border border-white/5 space-y-4">
                            <h2 className="text-lg font-semibold text-white">Results</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-1">Result Title</label>
                                    <input
                                        type="text"
                                        value={formData.results.title}
                                        onChange={(e) => handleNestedChange('results', 'title', e.target.value)}
                                        className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Result Images</label>
                                    <div className="grid grid-cols-2 gap-4 mb-4">
                                        {formData.results.images.map((img, idx) => (
                                            <div key={idx} className="relative group">
                                                <img src={img} alt={`Result ${idx + 1}`} className="w-full h-32 object-cover rounded-lg" />
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const newImages = formData.results.images.filter((_, i) => i !== idx);
                                                        handleNestedChange('results', 'images', newImages);
                                                    }}
                                                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <ImageUpload
                                        label="Add Result Image"
                                        value=""
                                        onChange={(url) => {
                                            if (url) {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    results: {
                                                        ...prev.results,
                                                        images: [...prev.results.images, url]
                                                    }
                                                }));
                                            }
                                        }}
                                    />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-400 mb-1">Result Description</label>
                                    <textarea
                                        value={formData.results.description}
                                        onChange={(e) => handleNestedChange('results', 'description', e.target.value)}
                                        rows={2}
                                        className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Key Items (Success Metrics)</label>
                                    <div className="space-y-3">
                                        {formData.results.items.length === 0 && (
                                            <button
                                                type="button"
                                                onClick={() => handleNestedChange('results', 'items', [''])}
                                                className="text-sm text-purple-400 hover:text-purple-300"
                                            >
                                                + Add Metric
                                            </button>
                                        )}
                                        {formData.results.items.map((item, idx) => (
                                            <div key={idx} className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={item}
                                                    onChange={(e) => handleResultItemChange(idx, e.target.value)}
                                                    placeholder={`Metric ${idx + 1}`}
                                                    className="flex-1 bg-black/20 border border-white/10 rounded-lg px-4 py-2 text-white text-sm"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const newItems = formData.results.items.filter((_, i) => i !== idx);
                                                        handleNestedChange('results', 'items', newItems);
                                                    }}
                                                    className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors border border-red-500/30"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                                {idx === formData.results.items.length - 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const newItems = [...formData.results.items, ''];
                                                            handleNestedChange('results', 'items', newItems);
                                                        }}
                                                        className="px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors border border-blue-500/30"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* FAQs */}
                        <div className="bg-[#1a1025] p-6 rounded-xl border border-white/5 space-y-4">
                            <h2 className="text-lg font-semibold text-white mb-4">FAQs</h2>
                            <div className="space-y-4">
                                {formData.faqs.length === 0 && (
                                    <div className="text-center py-4">
                                        <p className="text-gray-400 text-sm mb-3">No FAQs added yet.</p>
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, faqs: [...prev.faqs, { question: '', answer: '' }] }))}
                                            className="text-purple-400 hover:text-purple-300 text-sm font-medium"
                                        >
                                            + Add First FAQ
                                        </button>
                                    </div>
                                )}
                                {formData.faqs.map((faq, idx) => (
                                    <div key={idx} className="bg-black/20 p-4 rounded-lg border border-white/5 space-y-3 relative group">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const newFaqs = formData.faqs.filter((_, i) => i !== idx);
                                                setFormData(prev => ({ ...prev, faqs: newFaqs }));
                                            }}
                                            className="absolute top-2 right-2 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-500/10 rounded"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-400 mb-1">Question</label>
                                            <input
                                                type="text"
                                                value={faq.question}
                                                onChange={(e) => {
                                                    const newFaqs = [...formData.faqs];
                                                    newFaqs[idx].question = e.target.value;
                                                    setFormData(prev => ({ ...prev, faqs: newFaqs }));
                                                }}
                                                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-purple-500 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-400 mb-1">Answer</label>
                                            <textarea
                                                value={faq.answer}
                                                onChange={(e) => {
                                                    const newFaqs = [...formData.faqs];
                                                    newFaqs[idx].answer = e.target.value;
                                                    setFormData(prev => ({ ...prev, faqs: newFaqs }));
                                                }}
                                                rows={2}
                                                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-purple-500 outline-none"
                                            />
                                        </div>
                                    </div>
                                ))}
                                {formData.faqs.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, faqs: [...prev.faqs, { question: '', answer: '' }] }))}
                                        className="w-full py-2 border border-dashed border-white/20 rounded-lg text-gray-400 hover:text-white hover:border-white/40 transition-colors text-sm"
                                    >
                                        + Add Another FAQ
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end gap-4 pt-4 border-t border-white/10">
                            <button
                                type="button"
                                onClick={() => navigate('/admin/case-studies')}
                                className="px-6 py-2 bg-white/5 hover:bg-white/10 text-white font-medium rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className={`px-6 py-2 bg-purple hover:bg-purple/80 text-white font-medium rounded-lg transition-colors shadow-lg ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {loading ? 'Saving...' : (isEditMode ? 'Update Case Study' : 'Create Case Study')}
                            </button>
                        </div>
                    </form>
                </div>
        </AdminLayout>
    );
}
